import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Badge,
  Tooltip,
  message,
  Modal,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import type { KrybotTemplate } from '../../../types/krybot';
import { TemplateDetail } from './TemplateDetail';
import { TemplateForm } from './TemplateForm';
import styles from './TemplateManagement.module.css';

const { Search } = Input;
const { Option } = Select;

export const TemplateList: React.FC = () => {
  const [templates, setTemplates] = useState<KrybotTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedTemplate, setSelectedTemplate] = useState<KrybotTemplate | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<KrybotTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, [typeFilter, statusFilter]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await KrybotService.listTemplates();
      setTemplates(data);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to load templates';
      setError(errorMsg);
      message.error(`Failed to load templates: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowForm(true);
  };

  const handleEdit = (template: KrybotTemplate) => {
    setEditingTemplate(template);
    setShowForm(true);
  };

  const handleView = (template: KrybotTemplate) => {
    setSelectedTemplate(template);
    setShowDetail(true);
  };

  const handleFormClose = (saved: boolean) => {
    setShowForm(false);
    setEditingTemplate(null);
    if (saved) {
      loadTemplates();
    }
  };

  const handleDetailClose = () => {
    setShowDetail(false);
    setSelectedTemplate(null);
  };

  const filteredTemplates = templates.filter((template) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        template.template_key.toLowerCase().includes(searchLower) ||
        template.template_type?.toLowerCase().includes(searchLower) ||
        template.sector?.toLowerCase().includes(searchLower) ||
        template.prompt_text.toLowerCase().includes(searchLower)
      );
    }
    if (typeFilter !== 'all') {
      if (template.template_type !== typeFilter) return false;
    }
    if (statusFilter !== 'all') {
      if (statusFilter === 'active' && !template.is_active) return false;
      if (statusFilter === 'inactive' && template.is_active) return false;
    }
    return true;
  });

  const uniqueTypes = Array.from(new Set(templates.map((t) => t.template_type)));

  const columns = [
    {
      title: 'Template Key',
      dataIndex: 'template_key',
      key: 'template_key',
      render: (text: string, record: KrybotTemplate) => (
        <Button type="link" onClick={() => handleView(record)}>
          {text}
        </Button>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'template_type',
      key: 'template_type',
      render: (type: string) => <Tag>{type}</Tag>,
    },
    {
      title: 'Sector',
      dataIndex: 'sector',
      key: 'sector',
      render: (sector: string | undefined) => sector || <Tag>N/A</Tag>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, record: KrybotTemplate) => (
        <Badge status={record.is_active ? 'success' : 'default'} text={record.is_active ? 'Active' : 'Inactive'} />
      ),
    },
    {
      title: 'Versions',
      key: 'versions',
      render: (_: unknown, record: KrybotTemplate) => (
        <Tag>{record.versions?.length || 0}</Tag>
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: KrybotTemplate) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="template-list">
      <div className="template-list-header">
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
          <Space>
            <Search
              placeholder="Search templates..."
              allowClear
              style={{ width: 300 }}
              onSearch={setSearchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined />}
            />
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              style={{ width: 200 }}
            >
              <Option value="all">All Types</Option>
              {uniqueTypes.map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 150 }}
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadTemplates}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Create Template
            </Button>
          </Space>
        </Space>
      </div>

      {error && (
        <div style={{ marginBottom: 16, color: 'red' }}>
          Error: {error}
        </div>
      )}

      <Table
        columns={columns}
        dataSource={filteredTemplates}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} templates`,
        }}
      />

      {showDetail && selectedTemplate && (
        <Modal
          title={`Template: ${selectedTemplate.template_key}`}
          open={showDetail}
          onCancel={handleDetailClose}
          footer={null}
          width={1200}
          style={{ top: 20 }}
        >
          <TemplateDetail template={selectedTemplate} onClose={handleDetailClose} onRefresh={loadTemplates} />
        </Modal>
      )}

      {showForm && (
        <Modal
          title={editingTemplate ? 'Edit Template' : 'Create Template'}
          open={showForm}
          onCancel={() => handleFormClose(false)}
          footer={null}
          width={800}
        >
          <TemplateForm
            template={editingTemplate}
            onSave={() => handleFormClose(true)}
            onCancel={() => handleFormClose(false)}
          />
        </Modal>
      )}
    </div>
  );
};

